/**
 * Keeps the keyboard in a list whose items can leave it (W2-10 projects-data; DESIGN.md §4.1 keyboard).
 *
 * When the item that had focus leaves the list (moved to the Trash, restored, deleted for good: by a key, a menu,
 * a command or another tab) and the browser dropped focus to the page, focus moves to the same control of the next
 * item still shown (the one before it at the end of the list), or to `empty()` when no item is left. When focus is
 * somewhere else by then (the user moved on), nothing is taken from them. An action chosen in the item's menu or
 * a confirmation counts as the item's: the menu's focus return would land on the removed menu button.
 *
 * Items are elements with `data-project-id`; a control inside an item names itself with `data-focus-control`
 * (e.g. 'restore'). Spread the returned handlers on the list's root element.
 */

import { useLayoutEffect, useRef, type FocusEvent as ReactFocusEvent } from 'react';
import { successorOf } from './libraryModel';

export interface FocusHandoffOptions {
  /** Item ids in the order the list shows them. */
  readonly ids: readonly string[];
  /** The element of item `id` to focus; `control` is the control that had focus in the item that left. */
  readonly target: (id: string, control: string | null) => HTMLElement | null;
  /** Where the keyboard goes when no item is left; nothing happens without it. */
  readonly empty?: () => HTMLElement | null;
}

export interface FocusHandoffHandlers {
  readonly onFocus: (e: ReactFocusEvent<HTMLElement>) => void;
  readonly onBlur: (e: ReactFocusEvent<HTMLElement>) => void;
}

/** Overlays an item's action is chosen in (its menu, a confirmation, the command palette). */
const OVERLAY = '[role="menu"], [role="dialog"], [role="alertdialog"]';

interface Had {
  readonly id: string;
  readonly control: string | null;
}

/** True when nothing has focus any more (the focused element was removed, or only the page has focus). */
export function focusLost(): boolean {
  const active = document.activeElement;
  return active === null || active === document.body || !active.isConnected;
}

/** Focuses what `pick` returns on the next frame (after React re-rendered), unless focus went somewhere meanwhile. */
export function refocusIfLost(pick: () => HTMLElement | null | undefined): void {
  requestAnimationFrame(() => {
    if (focusLost()) pick()?.focus();
  });
}

/** The item a focus event happened in, and the named control in it. */
function itemOf(el: EventTarget | null): Had | null {
  if (!(el instanceof Element)) return null;
  const item = el.closest('[data-project-id]');
  const id = item?.getAttribute('data-project-id') ?? null;
  if (id === null) return null;
  return { id, control: el.closest('[data-focus-control]')?.getAttribute('data-focus-control') ?? null };
}

export function useFocusHandoff(opts: FocusHandoffOptions): FocusHandoffHandlers {
  const { ids, target, empty } = opts;
  // The item that has (or last had) focus inside the list; null when focus moved outside it.
  const had = useRef<Had | null>(null);
  // The ids as of the previous commit (the order the item that left had a place in).
  const before = useRef<readonly string[]>(ids);

  useLayoutEffect(() => {
    const prev = before.current;
    before.current = ids;
    const was = had.current;
    if (was === null || ids.includes(was.id)) return;
    had.current = null;
    // React removed the focused element in this commit: the browser left focus on the page.
    if (!focusLost()) return;
    const next = successorOf(prev, ids, was.id);
    const el = next === null ? (empty?.() ?? null) : target(next, was.control);
    el?.focus();
  }, [ids, target, empty]);

  return {
    onFocus: (e) => {
      // React also delivers focus inside portals rendered by an item (its open menu): not a move in the list.
      if (!(e.target instanceof Node) || !e.currentTarget.contains(e.target)) return;
      had.current = itemOf(e.target);
    },
    onBlur: (e) => {
      const to = e.relatedTarget;
      // Moving inside the list: onFocus records the new item. To nowhere (null): the item may be leaving the list
      // (removing a focused element blurs it in some browsers). Into a menu or dialog: the action chosen there may
      // remove the item, and the overlay's focus return goes to an element that is gone by then. Both keep it;
      // anywhere else forgets it.
      if (to === null || (to instanceof Node && e.currentTarget.contains(to))) return;
      if (to instanceof Element && to.closest(OVERLAY) !== null) return;
      had.current = null;
    },
  };
}
